import { useEffect, useState } from 'react'
import { useI18n, type MessageKey } from '../i18n'
import { navigate } from '../api/client'
import { fetchCycle, type CycleEvent, type CycleOverlay, type CyclePayload, type CycleReading } from '../api/cycle'

const ET = 'America/New_York'

function etDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: ET })
}

function etTime(iso: string): string {
  return `${new Date(iso).toLocaleTimeString('en-US', { timeZone: ET, hour: '2-digit', minute: '2-digit', hour12: false })} ET`
}

function fmtDay(ymd: string, language: string): string {
  return new Date(`${ymd}T12:00:00Z`).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
    month: 'short', day: 'numeric', weekday: 'short',
  })
}

// pct_rank_1y is the percentile of the *derived* quantity, so the sentence has
// to name which change it ranks.
const RANK_KEY: Record<string, 'events.reading.rank20d' | 'events.reading.rank5d' | 'events.reading.rankMonthly'> = {
  wti: 'events.reading.rank20d',
  dgs10: 'events.reading.rank5d',
  dgs2: 'events.reading.rank5d',
  cpi_mom: 'events.reading.rankMonthly',
  core_cpi_mom: 'events.reading.rankMonthly',
}

function readingChange(r: CycleReading): { text: string; up: boolean } | null {
  if (r.key === 'wti' && r.trend_20d_pct != null) {
    return { text: `${r.trend_20d_pct >= 0 ? '+' : ''}${r.trend_20d_pct.toFixed(1)}%`, up: r.trend_20d_pct >= 0 }
  }
  if (r.change_5d_bp != null) {
    return { text: `${r.change_5d_bp >= 0 ? '+' : ''}${r.change_5d_bp.toFixed(0)}bp`, up: r.change_5d_bp >= 0 }
  }
  return null
}

function ReadingCard({ reading }: { reading: CycleReading }) {
  const { language, t } = useI18n()
  const change = readingChange(reading)
  const rankKey = RANK_KEY[reading.key]
  return (
    <div className="cycle-reading">
      <p className="cycle-reading__label">{reading.label}</p>
      <p className="cycle-reading__value">
        {reading.value.toFixed(2)}<small>{reading.unit === '%' ? '%' : ` ${reading.unit}`}</small>
        {change && <em className={change.up ? 'up' : 'down'}>{change.up ? '▲' : '▼'} {change.text}</em>}
      </p>
      {rankKey && reading.pct_rank_1y != null && (
        <p className="muted small">{t(rankKey).replace('{n}', String(Math.round(reading.pct_rank_1y)))}</p>
      )}
      {reading.stat_month && <p className="muted small">{t('events.reading.statMonth').replace('{m}', reading.stat_month)}</p>}
      {reading.release_date && <p className="muted small">{t('events.reading.released').replace('{d}', fmtDay(reading.release_date.slice(0, 10), language))}</p>}
      <p className="muted small">{t('events.asOf').replace('{d}', reading.as_of.slice(0, 10))}</p>
    </div>
  )
}

// An ex_div overlay is a dividend date on a stock lot, not an earnings date:
// it gets its own sentence and carries the per-share payment the server sent.
function overlayText(t: (key: MessageKey) => string, overlay: CycleOverlay): string {
  const sym = overlay.symbol
  if (overlay.type === 'option_expiry') return t('events.overlay.expiry').replace('{sym}', sym)
  if (overlay.type === 'ex_div') {
    const line = t('events.overlay.exDiv').replace('{sym}', sym)
    return overlay.amount != null
      ? `${line}${t('events.overlay.exDivAmount').replace('{v}', overlay.amount.toFixed(2))}`
      : line
  }
  const owner = overlay.holding_id ? t('events.overlay.holding') : t('events.overlay.position')
  return `${t('events.overlay.earnings').replace('{sym}', sym)}${owner}`
}

function OverlayRow({ overlay, events }: { overlay: CycleOverlay; events: CycleEvent[] }) {
  const { t } = useI18n()
  const spanned = overlay.spans_event_id ? events.find((e) => e.id === overlay.spans_event_id) : undefined
  const base = overlayText(t, overlay)
  return (
    <a
      href="/positions"
      className="cycle-overlay"
      onClick={(e) => { e.preventDefault(); navigate('/positions') }}
    >
      {base}
      {spanned && <span className="badge badge--muted">{t('events.overlay.spans').replace('{label}', spanned.label)}</span>}
    </a>
  )
}

export function EventCyclePage({ signedIn }: { signedIn: boolean }) {
  const { language, t } = useI18n()
  const [data, setData] = useState<CyclePayload | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    fetchCycle(45)
      .then((res) => { if (!cancelled) setData(res) })
      .catch((err: Error) => { if (!cancelled) setError(err.message) })
    return () => { cancelled = true }
  }, [signedIn, language])

  const events = data?.events || []
  const overlays = data?.overlays || []
  const next = events[0]

  // One group per calendar day, events first and their overlays indented under
  // the same date.
  const days = Array.from(new Set([...events.map((e) => etDate(e.occurs_at)), ...overlays.map((o) => o.date)])).sort()

  return (
    <section className="scanner">
      <header className="scanner__head">
        <div>
          <p className="kicker">{t('events.kicker')}</p>
          <h1>{t('events.title')}</h1>
          <p className="muted">{t('events.subtitle')}</p>
        </div>
      </header>

      {error && <p className="form-message form-message--error">{error}</p>}

      {next && (
        <div className="cycle-next">
          <p className="cycle-next__label">{t('events.next.label')}</p>
          <p className="cycle-next__line">
            <strong>{next.label}</strong> · {fmtDay(etDate(next.occurs_at), language)} {etTime(next.occurs_at)}
            {next.days_until_trading != null && <> · {t('events.next.tradingDays').replace('{n}', String(next.days_until_trading))}</>}
          </p>
        </div>
      )}

      <h2 className="cycle-section-head">{t('events.timeline.title')}</h2>
      {days.length === 0 ? (
        <p className="muted">{t('events.empty')}</p>
      ) : (
        <ol className="cycle-timeline">
          {days.map((day) => (
            <li key={day}>
              <p className="cycle-timeline__date">{fmtDay(day, language)}</p>
              {events.filter((e) => etDate(e.occurs_at) === day).map((e) => (
                <div key={e.id} className="cycle-event">
                  <span className="pill pill--blue">{e.kind}</span>
                  <span className="cycle-event__label">{e.label}</span>
                  <span className="muted small">{etTime(e.occurs_at)}</span>
                  {e.impact && <span className="badge badge--muted">{e.impact}</span>}
                </div>
              ))}
              {overlays.filter((o) => o.date === day).map((o, i) => (
                <OverlayRow key={`${o.type}-${o.symbol}-${i}`} overlay={o} events={events} />
              ))}
            </li>
          ))}
        </ol>
      )}

      {signedIn && overlays.length === 0 && <p className="muted small">{t('events.overlay.empty')}</p>}

      <h2 className="cycle-section-head">{t('events.readings.title')}</h2>
      <div className="cycle-readings">
        {(data?.readings || []).map((r) => <ReadingCard key={r.key} reading={r} />)}
      </div>
    </section>
  )
}
